import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import { 
  ArrowLeft, User, Mail, Phone, Briefcase, DollarSign, Clock, 
  FileText, ExternalLink, Plus, Calendar, Video, MapPin
} from 'lucide-react';

const tagColors = {
  hot: 'bg-red-100 text-red-800',
  warm: 'bg-yellow-100 text-yellow-800',
  cold: 'bg-blue-100 text-blue-800'
};

const stageColors = {
  new: 'bg-gray-100 text-gray-800',
  HR_screen: 'bg-blue-100 text-blue-800',
  tech_round_1: 'bg-indigo-100 text-indigo-800',
  tech_round_2: 'bg-violet-100 text-violet-800',
  client_round: 'bg-purple-100 text-purple-800',
  HR_round: 'bg-pink-100 text-pink-800',
  offered: 'bg-amber-100 text-amber-800',
  accepted: 'bg-emerald-100 text-emerald-800',
  joined: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  declined: 'bg-orange-100 text-orange-800'
};

const decisionColors = {
  pending: 'bg-gray-100 text-gray-800',
  move_forward: 'bg-green-100 text-green-800',
  hold: 'bg-yellow-100 text-yellow-800',
  reject: 'bg-red-100 text-red-800',
  offer: 'bg-purple-100 text-purple-800'
};

export default function CandidateDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requisitions, setRequisitions] = useState([]);
  const [showAddAppDialog, setShowAddAppDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [selectedRequisitionId, setSelectedRequisitionId] = useState('');
  const [selectedApplicationId, setSelectedApplicationId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [interviewForm, setInterviewForm] = useState({
    round_type: 'HR_screen',
    round_name: '',
    scheduled_at: '',
    duration_minutes: 60,
    mode: 'online',
    meeting_link: '',
    location: '',
    interviewer_ids: []
  });

  const [employees, setEmployees] = useState([]);

  const fetchCandidate = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/recruitment/candidates/${id}`);
      setCandidate(response.data);
    } catch (error) {
      console.error('Error fetching candidate:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequisitions = async () => {
    try {
      const response = await api.get('/recruitment/requisitions', { params: { status: 'open', limit: 100 } });
      setRequisitions(response.data.requisitions || []);
    } catch (error) {
      console.error('Error fetching requisitions:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  useEffect(() => {
    fetchCandidate();
    fetchRequisitions();
    fetchEmployees();
  }, [id]);

  const handleAddApplication = async () => {
    if (!selectedRequisitionId) return;
    setSubmitting(true);
    try {
      await api.post('/recruitment/applications', {
        requisition_id: selectedRequisitionId,
        candidate_id: id
      });
      setShowAddAppDialog(false);
      setSelectedRequisitionId('');
      fetchCandidate();
    } catch (error) {
      console.error('Error adding application:', error);
      alert(error.response?.data?.detail || 'Failed to add application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleScheduleInterview = async () => {
    if (!selectedApplicationId || !interviewForm.scheduled_at) return;
    setSubmitting(true);
    try {
      await api.post('/recruitment/interviews', {
        application_id: selectedApplicationId,
        ...interviewForm,
        send_notifications: true
      });
      setShowScheduleDialog(false);
      setInterviewForm({
        round_type: 'HR_screen',
        round_name: '',
        scheduled_at: '',
        duration_minutes: 60,
        mode: 'online',
        meeting_link: '',
        location: '',
        interviewer_ids: []
      });
      fetchCandidate();
    } catch (error) {
      console.error('Error scheduling interview:', error);
      alert(error.response?.data?.detail || 'Failed to schedule interview');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-16">Loading...</div>;
  }

  if (!candidate) {
    return <div className="text-center py-16">Candidate not found</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/recruitment/candidates')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="col-span-2">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl flex items-center gap-2">
                    {candidate.name}
                    {candidate.tags?.map((tag, idx) => (
                      <Badge key={idx} className={tagColors[tag]}>{tag}</Badge>
                    ))}
                  </CardTitle>
                  <CardDescription>
                    {candidate.current_designation || 'N/A'} at {candidate.current_company || 'N/A'}
                  </CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                {candidate.resume_url && (
                  <a href={candidate.resume_url} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline">
                      <FileText className="w-4 h-4 mr-2" />
                      View Resume
                    </Button>
                  </a>
                )}
                {candidate.devops_work_item_url && (
                  <a href={candidate.devops_work_item_url} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      DevOps
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span>{candidate.email}</span>
                </div>
                {candidate.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{candidate.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-gray-400" />
                  <span>{candidate.years_experience || 0} years experience</span>
                </div>
                {candidate.notice_period && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span>{candidate.notice_period} days notice period</span>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <span>Current CTC: {candidate.current_ctc ? `₹${candidate.current_ctc} LPA` : 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <span>Expected CTC: {candidate.expected_ctc ? `₹${candidate.expected_ctc} LPA` : 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{candidate.source?.replace('_', ' ')}</Badge>
                  {candidate.source_details && (
                    <span className="text-sm text-gray-500">({candidate.source_details})</span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h4 className="font-medium mb-2">Skills</h4>
              <div className="flex flex-wrap gap-2">
                {candidate.skills?.map((skill, idx) => (
                  <Badge key={idx} variant="outline">{skill}</Badge>
                ))}
              </div>
            </div>

            {candidate.notes && (
              <div className="mt-6">
                <h4 className="font-medium mb-2">Notes</h4>
                <p className="text-gray-600">{candidate.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions Card */}
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Dialog open={showAddAppDialog} onOpenChange={setShowAddAppDialog}>
              <DialogTrigger asChild>
                <Button className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Apply to Position
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Apply to Position</DialogTitle>
                  <DialogDescription>Select an open position for this candidate</DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <Label>Select Position</Label>
                  <Select value={selectedRequisitionId} onValueChange={setSelectedRequisitionId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a position" />
                    </SelectTrigger>
                    <SelectContent>
                      {requisitions.map((req) => (
                        <SelectItem key={req.id} value={req.id}>
                          {req.title} - {req.department}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddAppDialog(false)}>Cancel</Button>
                  <Button onClick={handleAddApplication} disabled={!selectedRequisitionId || submitting}>
                    {submitting ? 'Adding...' : 'Apply'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {candidate.applications?.length > 0 && (
              <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full">
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Interview
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Schedule Interview</DialogTitle>
                    <DialogDescription>Schedule an interview round</DialogDescription>
                  </DialogHeader>
                  <div className="py-4 space-y-4">
                    <div>
                      <Label>Application</Label>
                      <Select value={selectedApplicationId} onValueChange={setSelectedApplicationId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select application" />
                        </SelectTrigger>
                        <SelectContent>
                          {candidate.applications?.filter(a => a.is_active).map((app) => (
                            <SelectItem key={app.id} value={app.id}>
                              {app.requisition_title} ({app.current_stage})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Round Type</Label>
                        <Select
                          value={interviewForm.round_type}
                          onValueChange={(v) => setInterviewForm(prev => ({ ...prev, round_type: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="HR_screen">HR Screening</SelectItem>
                            <SelectItem value="tech_round_1">Technical Round 1</SelectItem>
                            <SelectItem value="tech_round_2">Technical Round 2</SelectItem>
                            <SelectItem value="client_round">Client Round</SelectItem>
                            <SelectItem value="HR_round">HR Final Round</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Mode</Label>
                        <Select
                          value={interviewForm.mode}
                          onValueChange={(v) => setInterviewForm(prev => ({ ...prev, mode: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="online">Online</SelectItem>
                            <SelectItem value="offline">Offline</SelectItem>
                            <SelectItem value="phone">Phone</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Date & Time</Label>
                      <Input
                        type="datetime-local"
                        value={interviewForm.scheduled_at}
                        onChange={(e) => setInterviewForm(prev => ({ ...prev, scheduled_at: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Duration (minutes)</Label>
                      <Input
                        type="number"
                        value={interviewForm.duration_minutes}
                        onChange={(e) => setInterviewForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) }))}
                      />
                    </div>
                    {interviewForm.mode === 'online' && (
                      <div>
                        <Label>Meeting Link</Label>
                        <Input
                          value={interviewForm.meeting_link}
                          onChange={(e) => setInterviewForm(prev => ({ ...prev, meeting_link: e.target.value }))}
                          placeholder="https://meet.google.com/..."
                        />
                      </div>
                    )}
                    {interviewForm.mode === 'offline' && (
                      <div>
                        <Label>Location</Label>
                        <Input
                          value={interviewForm.location}
                          onChange={(e) => setInterviewForm(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="Conference Room A"
                        />
                      </div>
                    )}
                    <div>
                      <Label>Interviewers</Label>
                      <Select
                        value={interviewForm.interviewer_ids[0] || ''}
                        onValueChange={(v) => setInterviewForm(prev => ({ ...prev, interviewer_ids: [v] }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select interviewer" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map((emp) => (
                            <SelectItem key={emp.id} value={emp.user_id || emp.id}>
                              {emp.name} - {emp.designation}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
                    <Button 
                      onClick={handleScheduleInterview} 
                      disabled={!selectedApplicationId || !interviewForm.scheduled_at || submitting}
                    >
                      {submitting ? 'Scheduling...' : 'Schedule'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Applications */}
      <Card>
        <CardHeader>
          <CardTitle>Applications ({candidate.applications?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {!candidate.applications?.length ? (
            <div className="text-center py-8 text-gray-500">
              No applications yet. Apply this candidate to a position.
            </div>
          ) : (
            <div className="space-y-4">
              {candidate.applications.map((app) => (
                <div key={app.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold">{app.requisition_title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={stageColors[app.current_stage]}>
                          {app.current_stage?.replace('_', ' ')}
                        </Badge>
                        {!app.is_active && (
                          <Badge variant="destructive">Inactive</Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      <div>{app.total_interviews || 0} interviews</div>
                      <div>Applied: {new Date(app.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                  
                  {/* Stage History */}
                  {app.stage_history?.length > 0 && (
                    <div className="mt-4">
                      <h5 className="text-sm font-medium mb-2">Stage History</h5>
                      <div className="flex flex-wrap gap-2">
                        {app.stage_history.map((sh, idx) => (
                          <div key={idx} className="text-xs bg-gray-100 rounded px-2 py-1">
                            {sh.stage?.replace('_', ' ')} 
                            <span className="text-gray-400 ml-1">
                              {new Date(sh.entered_at).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Interviews */}
                  {app.interviews?.length > 0 && (
                    <div className="mt-4">
                      <h5 className="text-sm font-medium mb-2">Interviews</h5>
                      <div className="space-y-2">
                        {app.interviews.map((interview) => (
                          <div key={interview.id} className="flex items-center justify-between bg-gray-50 rounded p-2">
                            <div className="flex items-center gap-2">
                              {interview.mode === 'online' ? (
                                <Video className="w-4 h-4 text-blue-500" />
                              ) : (
                                <MapPin className="w-4 h-4 text-green-500" />
                              )}
                              <span className="text-sm font-medium">{interview.round_name || interview.round_type?.replace('_', ' ')}</span>
                              <span className="text-xs text-gray-500">
                                {new Date(interview.scheduled_at).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {interview.overall_score && (
                                <Badge variant="outline">Score: {interview.overall_score.toFixed(1)}/5</Badge>
                              )}
                              <Badge className={decisionColors[interview.overall_decision]}>
                                {interview.overall_decision}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Offer */}
                  {app.offer && (
                    <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                      <h5 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Offer Details
                      </h5>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">CTC:</span> ₹{app.offer.offered_ctc} LPA
                        </div>
                        <div>
                          <span className="text-gray-500">Status:</span>{' '}
                          <Badge variant="outline">{app.offer.approval_status}</Badge>
                        </div>
                        <div>
                          <span className="text-gray-500">Decision:</span>{' '}
                          <Badge variant="outline">{app.offer.candidate_decision}</Badge>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
